
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type RoofingService = {
  id: string;
  category: string;
  service_name: string;
  description: string;
  seo_keywords: string[];
  reference_urls?: string[];
  created_at: string;
  updated_at: string;
};

export type ServicesByCategory = {
  [category: string]: {
    description: string;
    services: {
      name: string;
      description: string;
    }[];
  };
};

// Descriptions for each category
const categoryDescriptions: Record<string, string> = {
  'Solar Integration & Eco-Friendly Options': 'Sustainable and energy-efficient roofing solutions that save money and protect the environment',
  'Insurance Process Education & Support': 'Expert guidance through the complex insurance claims process for roof damage',
  'Specialized Residential Roofing Materials': 'Premium materials that enhance curb appeal and provide superior protection',
  'Commercial-Specific Roofing Services': 'Professional roofing for businesses and organizations',
  'Preventative Maintenance Programs': 'Keep your roof in optimal condition with our proactive maintenance solutions',
  'Cultural & Language Services': 'Inclusive roofing services with multilingual communication options',
  'Financing & Payment Solutions': 'Flexible payment options to make quality roofing affordable',
  'Digital & Technology Services': 'Advanced tools and technology for precise assessment and planning'
};

// Fetch all roofing services
export const useRoofingServices = () => {
  return useQuery({
    queryKey: ['roofingServices'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('roofing_services')
        .select('*')
        .order('category', { ascending: true })
        .order('service_name', { ascending: true });

      if (error) throw new Error(error.message);
      return data as RoofingService[];
    },
  });
};

// Group services by category for easier display
export const useServicesByCategory = () => {
  const servicesQuery = useRoofingServices();

  const servicesByCategory: ServicesByCategory = {};
  
  if (servicesQuery.data) {
    servicesQuery.data.forEach(service => {
      if (!servicesByCategory[service.category]) {
        servicesByCategory[service.category] = {
          description: categoryDescriptions[service.category] || '',
          services: []
        };
      }
      
      servicesByCategory[service.category].services.push({
        name: service.service_name,
        description: service.description
      });
    });
  }
  
  return {
    servicesByCategory,
    isLoading: servicesQuery.isLoading,
    isError: servicesQuery.isError,
    error: servicesQuery.error
  };
};
